import React from 'react';
import { Divider, Tab, Tabs } from '@mui/material';
import VeraAdminChatAssistPromptSandbox from './VeraAdminChatAssistPromptSandbox';
import InvestorContactSelector from './VeraGlobalChatPromptOverrideAdmin/InvestorContactSelector';
import InvestorDraftBanner from './VeraGlobalChatPromptOverrideAdmin/InvestorDraftBanner';
import InvestorGoogleSearchToggle from './VeraGlobalChatPromptOverrideAdmin/InvestorGoogleSearchToggle';
import InvestorGroundingPromptTab from './VeraGlobalChatPromptOverrideAdmin/InvestorGroundingPromptTab';
import InvestorIntentPromptTab from './VeraGlobalChatPromptOverrideAdmin/InvestorIntentPromptTab';
import InvestorPreviewChatSection from './VeraGlobalChatPromptOverrideAdmin/InvestorPreviewChatSection';
import InvestorProposalFooter from './VeraGlobalChatPromptOverrideAdmin/InvestorProposalFooter';
import InvestorProposalLinkBanner from './VeraGlobalChatPromptOverrideAdmin/InvestorProposalLinkBanner';
import InvestorSystemPromptTab from './VeraGlobalChatPromptOverrideAdmin/InvestorSystemPromptTab';
import { useVeraGlobalChatPromptOverride } from './VeraGlobalChatPromptOverrideAdmin/useVeraGlobalChatPromptOverride';
import StyledFieldDescription from '../styles/StyledFieldDescription';
import StyledSection from '../styles/StyledSection';
import { StyledHeading, StyledTypography } from '../styles/styles';

export default function VeraGlobalChatPromptOverrideAdmin() {
  const sandbox = useVeraGlobalChatPromptOverride();

  return (
    <StyledSection tone="tone1">
      <StyledHeading tone="tone1" headerVariant="heading2">
        Vera Prompt Sandbox
      </StyledHeading>
      <StyledFieldDescription stacked>
        {sandbox.productTab === 0 ? (
          <>
            Production investor chat uses <code>src/services/vera-system-instruction.md</code> and{' '}
            <code>src/services/vera-intent-instruction.md</code>. Test changes here and submit
            proposals for engineering review.
          </>
        ) : (
          <>
            Production admin assist uses{' '}
            <code>src/services/vera-admin-chat-assist-instruction.md</code> and{' '}
            <code>src/services/vera-admin-chat-assist-intent-instruction.md</code>. Test coaching
            and classifier prompts here with a simulated investor/admin transcript.
          </>
        )}
      </StyledFieldDescription>
      <StyledTypography component="p" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
        Proposal notification channel settings are managed in Global Configuration → Vera → Prompt
        Proposals.
      </StyledTypography>

      <InvestorProposalLinkBanner
        proposalId={sandbox.proposalId}
        linkedProposalLoading={sandbox.linkedProposalLoading}
        linkedProposal={sandbox.linkedProposal}
        onCopyToEditor={sandbox.copyProposalToEditor}
      />

      {sandbox.productTab === 0 && sandbox.savedDraft && (
        <InvestorDraftBanner
          savedDraft={sandbox.savedDraft}
          onRestore={sandbox.restoreDraft}
          onDismiss={sandbox.dismissDraft}
        />
      )}

      <Tabs
        value={sandbox.productTab}
        onChange={(_, value) => sandbox.setProductTab(value)}
        sx={{
          mb: 3,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48 },
        }}
      >
        <Tab label="Vera Investor Chat" />
        <Tab label="Vera Admin Assist" />
      </Tabs>

      {sandbox.productTab === 0 && (
        <>
          <InvestorGoogleSearchToggle
            globalSearchEnabled={sandbox.globalSearchEnabled}
            sandboxSearchEnabled={sandbox.sandboxSearchEnabled}
            sandboxSearchLocked={sandbox.sandboxSearchLocked}
            onToggle={sandbox.handleSandboxSearchToggle}
          />

          <Tabs
            value={sandbox.investorPromptTab}
            onChange={(_, value) => sandbox.setInvestorPromptTab(value)}
            sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Tab label="System Prompt" />
            <Tab label="Intent Prompt" />
            {sandbox.showGroundingPromptTab && <Tab label="Google Search Prompt" />}
          </Tabs>

          {sandbox.investorPromptTab === 0 && (
            <InvestorSystemPromptTab
              markdown={sandbox.markdown}
              rawMode={sandbox.rawMode}
              onRawModeChange={sandbox.setRawMode}
              onMarkdownChange={sandbox.handleRawMarkdownChange}
              onRawBlur={sandbox.syncEditorFromMarkdown}
              editor={sandbox.editor}
              globalGuideOptions={sandbox.globalGuideOptions}
            />
          )}

          {sandbox.investorPromptTab === 1 && (
            <InvestorIntentPromptTab
              intentText={sandbox.intentText}
              intentProductionText={sandbox.intentProductionText}
              globalGuideOptions={sandbox.globalGuideOptions}
              onIntentChange={sandbox.handleIntentChange}
              onResetToProduction={sandbox.resetIntentToProduction}
            />
          )}

          {sandbox.investorPromptTab === 2 && sandbox.showGroundingPromptTab && (
            <InvestorGroundingPromptTab
              groundingText={sandbox.groundingText}
              groundingProductionText={sandbox.groundingProductionText}
              globalGuideOptions={sandbox.globalGuideOptions}
              onGroundingChange={sandbox.handleGroundingChange}
              onResetToProduction={sandbox.resetGroundingToProduction}
            />
          )}

          <Divider sx={{ mb: 3 }} />

          <InvestorContactSelector
            contactOptions={sandbox.contactOptions}
            contactLoading={sandbox.contactLoading}
            selectedContact={sandbox.selectedContact}
            onSearchChange={sandbox.setContactSearch}
            onContactChange={sandbox.setSelectedContact}
          />

          <Divider sx={{ mb: 3 }} />

          <InvestorPreviewChatSection
            chatId={sandbox.chatId}
            markdown={sandbox.markdown}
            selectedContact={sandbox.selectedContact}
            contactInvestments={sandbox.contactInvestments}
            selectedInvestor={sandbox.selectedInvestor}
            fundSearch={sandbox.fundSearch}
            messages={sandbox.messages}
            isProcessing={sandbox.isProcessing}
            onFundSearchChange={sandbox.setFundSearch}
            onInvestorChange={sandbox.setSelectedInvestor}
            onSendMessage={sandbox.sendMessage}
            onResetChat={sandbox.resetChat}
          />

          <Divider sx={{ mb: 3 }} />

          <InvestorProposalFooter
            saving={sandbox.saving}
            error={sandbox.error}
            submittedProposal={sandbox.submittedProposal}
            markdown={sandbox.markdown}
            isDirty={sandbox.isDirty}
            intentIsDirty={sandbox.intentIsDirty}
            groundingIsDirty={sandbox.groundingIsDirty}
            promptSynced={sandbox.promptSynced}
            intentSynced={sandbox.intentSynced}
            groundingSynced={sandbox.groundingSynced}
            showGroundingPromptTab={sandbox.showGroundingPromptTab}
            globalGuideOptions={sandbox.globalGuideOptions}
            onSubmitProposal={sandbox.submitProposal}
          />
        </>
      )}

      {sandbox.productTab === 1 && <VeraAdminChatAssistPromptSandbox />}
    </StyledSection>
  );
}
